/**
 * dataRights.ts — Inzage en verwijdering (AVG art. 15 en 17).
 *
 * De database kon dit al: `export_learner_data()` en `delete_learner_data()`
 * bestaan sinds 0006 en zijn in 0021 gerepareerd. Alleen riep niets in de app
 * ze aan. Een recht dat technisch werkt maar nergens een knop heeft, is voor
 * de ouder die het wil uitoefenen niet te onderscheiden van een recht dat er
 * niet is.
 *
 * WAAROM HIER GEEN AUTORISATIECONTROLE STAAT
 *
 * Beide functies controleren serverside zelf of de aanroeper een gekoppelde
 * ouder of leerkracht is (`can_view_learner` / `is_parent_of`). Een tweede
 * controle in de client zou een afwijkende waarheid worden die uit de pas kan
 * gaan lopen met de policy — en zou bovendien niets tegenhouden, want wie de
 * client omzeilt komt alsnog bij dezelfde RPC uit.
 *
 * WAT DE EXPORT NIET BEVAT
 *
 * De hash van de herstelcode. Die valt bewust buiten de export: hij is geen
 * persoonsgegeven van het kind maar een authenticatiegeheim, en meesturen zou
 * het exportbestand tot een aanvalsdoel maken.
 */

import { supabase } from './client';
import { error as logError } from '../../utils/logger';
export interface ExportResult {
  ok: boolean;
  message?: string;
  data?: unknown;
}
export interface DeleteResult {
  ok: boolean;
  message?: string;
  /** Wat er verwijderd is, per tabel. Verantwoordingsplicht, art. 5 lid 2. */
  deleted?: Record<string, number>;
}

/**
 * Haalt alle gegevens van dit kind op als één JSON-object.
 *
 * Levert de ruwe data terug in plaats van meteen een bestand te downloaden,
 * zodat de aanroeper kan bepalen wat er mee gebeurt — tonen, downloaden of
 * mailen zijn drie verschillende beslissingen.
 */
export async function exportLearnerData(learnerId: string): Promise<ExportResult> {
  const {
    data,
    error
  } = await supabase.rpc('export_learner_data', {
    p_learner_id: learnerId
  });
  if (error) {
    logError('[DataRights] Export mislukt', error);
    return {
      ok: false,
      message: 'Het ophalen van de gegevens is niet gelukt. Controleer of dit kind aan je account gekoppeld is en probeer het opnieuw.'
    };
  }
  return {
    ok: true,
    data
  };
}

/**
 * Verwijdert de gegevens van dit kind.
 *
 * `deleteProfile` bepaalt of het account zelf ook weg gaat. Dat is bewust een
 * aparte keuze: "wis wat je van mijn kind weet" en "hef het account op" zijn
 * twee verschillende verzoeken, en ze samenvoegen betekent dat een ouder die
 * alleen wil opschonen zijn kind ook zijn inlog afneemt.
 */
export async function deleteLearnerData(learnerId: string, deleteProfile: boolean): Promise<DeleteResult> {
  const {
    data,
    error
  } = await supabase.rpc('delete_learner_data', {
    p_learner_id: learnerId,
    p_delete_profile: deleteProfile
  });
  if (error) {
    logError('[DataRights] Verwijderen mislukt', error);
    return {
      ok: false,
      message: 'Het verwijderen is niet gelukt. Er is niets gewist. Probeer het opnieuw.'
    };
  }
  return {
    ok: true,
    deleted: (data ?? {}) as Record<string, number>
  };
}

/**
 * Zet een exportresultaat om in een download.
 *
 * Een leesbaar bestand met inspringing, niet de compacte vorm: dit bestand is
 * bedoeld om door een mens gelezen te worden, en een AVG-inzageverzoek dat
 * beantwoord wordt met één regel JSON voldoet naar de letter maar niet naar
 * de bedoeling.
 */
export function downloadExport(data: unknown, learnerName: string): void {
  const stamp = new Date().toISOString().slice(0, 10);
  const safeName = learnerName.replace(/[^\p{L}\p{N}]+/gu, '-').toLowerCase();
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `meesterdjay-gegevens-${safeName}-${stamp}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}